import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import BaseUrl from '../BaseUrl';
import { getAuthConfig } from '../services/auth';

const ProfileContext = createContext(null);

const CACHE_TTL_MS = 5 * 60 * 1000;
let memoryCache = null;
let memoryCacheAt = 0;
let inflightRequest = null;

function readStoredProfile() {
  try {
    const raw = localStorage.getItem('profile');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredProfile(profile) {
  if (profile) {
    localStorage.setItem('profile', JSON.stringify(profile));
  }
}

function isCacheFresh() {
  return memoryCache && Date.now() - memoryCacheAt < CACHE_TTL_MS;
}

export function invalidateProfileCache() {
  memoryCache = null;
  memoryCacheAt = 0;
  inflightRequest = null;
}

export function ProfileProvider({ children }) {
  const [profile, setProfile] = useState(() => readStoredProfile() || memoryCache || {});
  const [loading, setLoading] = useState(false);

  const applyProfile = useCallback((next) => {
    if (!next) return;
    memoryCache = next;
    memoryCacheAt = Date.now();
    writeStoredProfile(next);
    setProfile(next);
  }, []);

  const fetchProfile = useCallback(async (force = false) => {
    if (!force && isCacheFresh()) {
      setProfile(memoryCache);
      return memoryCache;
    }

    const stored = readStoredProfile();
    if (!force && stored && !memoryCache) {
      memoryCache = stored;
      memoryCacheAt = Date.now();
      setProfile(stored);
    }

    if (!force && inflightRequest) {
      return inflightRequest;
    }

    const token = localStorage.getItem('authorization');
    if (!token) {
      return null;
    }

    const url = BaseUrl() + 'api/user/get-user-profile';

    setLoading(true);
    inflightRequest = axios
      .get(url, getAuthConfig())
      .then((res) => {
        if (res.data.ResponseCode == 200) {
          applyProfile(res.data.Response);
          return res.data.Response;
        }
        return stored || memoryCache;
      })
      .catch((error) => {
        console.log(error);
        return stored || memoryCache;
      })
      .finally(() => {
        setLoading(false);
        inflightRequest = null;
      });

    return inflightRequest;
  }, [applyProfile]);

  const value = useMemo(
    () => ({
      profile,
      loading,
      fetchProfile,
      setProfile: applyProfile,
      refreshProfile: () => fetchProfile(true),
    }),
    [profile, loading, fetchProfile, applyProfile],
  );

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) {
    throw new Error('useProfile must be used within ProfileProvider');
  }
  return ctx;
}

export default ProfileContext;
